import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { TablePage } from "@outpave/ds";
import useDebouncedSearch from "hooks/useDebouncedSearch";
import SubscriptionsTable, {
  SubscriptionFilterCriteriaProps,
} from "components/admin/subscriptions/SubscriptionsTable";
import SubscriptionDetailsDrawer from "components/admin/subscriptions/SubscriptionDetailsDrawer";
import {
  LISTABLE_SUBSCRIPTION_STATUSES,
  SubscriptionStatus,
} from "enums/Subscription";
import { useCheckUserRole } from "hooks/useCheckUserRole";
import useGetParamFromUrl from "hooks/useGetParamFromUrl";
import Role from "enums/Role";

const Subscriptions: React.FC = () => {
  const { t } = useTranslation();
  const isAdminOrHigher = useCheckUserRole(Role.Admin);

  const [activeTab, setActiveTab] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const search = useDebouncedSearch(setSearchTerm);
  const [refreshIndex, setRefreshIndex] = useState(0);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const [filterCriteria, setFilterCriteria] =
    useState<SubscriptionFilterCriteriaProps>({
      status: [SubscriptionStatus.ACTIVE, SubscriptionStatus.PAST_DUE],
      searchTerm: "",
    });

  const tabConfigurations = [
    {
      label: t("Active"),
      status: [
        SubscriptionStatus.ACTIVE,
        SubscriptionStatus.PAST_DUE,
        SubscriptionStatus.INCOMPLETE,
      ],
    },
    { label: t("Canceled"), status: [SubscriptionStatus.CANCELED] },
    { label: t("Show All"), status: LISTABLE_SUBSCRIPTION_STATUSES },
  ];

  useEffect(() => {
    setFilterCriteria({
      status: tabConfigurations[activeTab].status,
      searchTerm,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, searchTerm]);

  const handleRefresh = () => setRefreshIndex((previous) => previous + 1);

  const handleViewDetails = (subscriptionId: string) => {
    setSelectedId(subscriptionId);
    setDrawerOpen(true);
  };

  const resetParam = useGetParamFromUrl("id", handleViewDetails);

  const handleCloseDrawer = (refresh: boolean = false) => {
    if (refresh) handleRefresh();
    resetParam();
    setSelectedId(null);
    setDrawerOpen(false);
  };

  return (
    <TablePage
      title={t("Subscriptions")}
      tabs={{
        items: tabConfigurations.map((tab, index) => ({
          id: String(index),
          label: t(tab.label),
        })),
        activeId: String(activeTab),
        onChange: (id) => setActiveTab(Number(id)),
      }}
      search={search}
    >
      <>
        <SubscriptionsTable
          filterCriteria={filterCriteria}
          refreshIndex={refreshIndex}
          readOnly={!isAdminOrHigher}
          onViewDetails={handleViewDetails}
          onRefresh={handleRefresh}
        />

        {selectedId && (
          <SubscriptionDetailsDrawer
            open={drawerOpen}
            subscriptionId={selectedId}
            readOnly={!isAdminOrHigher}
            onClose={handleCloseDrawer}
          />
        )}
      </>
    </TablePage>
  );
};

export default Subscriptions;
